// ============================================================
// TMR Portal Admin — como as regras de negócio se leem na tela (25)
// ============================================================
import type {
  BusinessRule,
  CompanyBusinessRule,
  CompanyBusinessRules,
  RuleValue,
  RuleValueType,
  ScoreBandValue,
} from './contract';

/** The custom down payment rule — how many payments, counting the down payment, unlock a custom one. */
export const CUSTOM_DOWN_PAYMENT_KEY = 'vendas.entrada_personalizada.parcelas_minimas';

/**
 * The sales module rule — which screens a clinic sells with: the legacy Produtos/Ofertas/Gestão de Link, the
 * new Vendas, or both during the transition (`25` §8).
 */
export const SALES_MODULE_KEY = 'portal.modulos.vendas';

/** Whether the credit rating holds a sale back — off it only records, as a dry run (`26` §8). */
export const CREDIT_RATING_MODE_KEY = 'vendas.rating.modo';

/** How many days after the due date an open installment counts as a delinquent debt for the rating. */
export const DELINQUENCY_DAYS_KEY = 'vendas.rating.dias_inadimplencia';

/** What the rating does with a patient who has a delinquent debt anywhere in the TMR. */
export const DELINQUENT_DEBT_ACTION_KEY = 'vendas.rating.acao_divida_inadimplente';

/** The action for each band of the bureau score. */
export const SCORE_BANDS_KEY = 'vendas.rating.faixas_score';

/**
 * The withdrawal period — how many calendar days after the order is made effective the patient may withdraw
 * with a refund. Every cancellation of the v1.1 reads it at once.
 */
export const WITHDRAWAL_PERIOD_KEY = 'cancelamento.prazo_arrependimento.dias';

/** The four credit rating rules, in the order the Crédito → Regras page lists them (`26` §9). */
export const CREDIT_RATING_KEYS = [
  CREDIT_RATING_MODE_KEY,
  DELINQUENCY_DAYS_KEY,
  DELINQUENT_DEBT_ACTION_KEY,
  SCORE_BANDS_KEY,
] as const;

/** How one option of a choice rule reads: its name and what choosing it does. */
export interface RuleOptionCopy {
  label: string;
  description: string;
}

/**
 * How each option of a choice rule reads.
 *
 * The backend sends the options as the strings the v1.1 compares against (`"novo"`); what the operator reads
 * is product copy, and product copy lives here (`25` §13). "novo" reads "Vendas" because that is the name of
 * the screen the clinic gets — "novo" only means something next to the code that reads it.
 */
const RATING_ACTION_COPY: Record<string, RuleOptionCopy> = {
  liberar: {
    label: 'Liberar',
    description: 'A venda segue: a clínica envia a proposta ou define a condição.',
  },
  aprovacao_manual: {
    label: 'Aprovação manual',
    description: 'A venda espera a TMR aprovar ou reprovar em Crédito → Aprovação manual. A clínica continua montando.',
  },
};

const OPTION_COPY: Record<string, Record<string, RuleOptionCopy>> = {
  [CREDIT_RATING_MODE_KEY]: {
    desligado: {
      label: 'Desligado',
      description: 'O rating é calculado e gravado, mas não trava nada. Serve para calibrar as faixas com vendas reais.',
    },
    ativo: {
      label: 'Ativo',
      description: 'A venda só envia proposta ou define condição com o rating liberado; o que ele não liberar vai para aprovação manual.',
    },
  },
  [DELINQUENT_DEBT_ACTION_KEY]: RATING_ACTION_COPY,
  [SCORE_BANDS_KEY]: RATING_ACTION_COPY,
  [SALES_MODULE_KEY]: {
    legado: {
      label: 'Legado',
      description: 'Produtos, Ofertas e Gestão de Link. Uma clínica que já usou a Vendas deixa de ver as vendas feitas nela.',
    },
    novo: {
      label: 'Vendas',
      description: 'Vendas e Simulador. Produto e oferta novos passam a ser recusados.',
    },
    ambos: {
      label: 'Ambos',
      description: 'Os dois menus, na transição. Use para tirar da Vendas uma clínica com venda em andamento.',
    },
  },
};

/**
 * One option as the operator reads it. An option this screen has no copy for — the backend added it first —
 * reads as itself rather than disappearing.
 *
 * @param rule The rule, for its key.
 * @param option The option as the API sends it.
 */
export function ruleOptionCopy(rule: { key: string }, option: string): RuleOptionCopy {
  return OPTION_COPY[rule.key]?.[option] ?? { label: option, description: '' };
}

/**
 * How a number reads for the rules whose number alone would be ambiguous.
 *
 * The custom down payment rule counts the down payment, which is exactly what an operator gets wrong: "3"
 * reads as "three installments" when it means "entrada + 2x". Spelling the split out beside the number is
 * the whole point of the label.
 */
const VALUE_DETAIL: Record<string, (value: number) => string> = {
  [CUSTOM_DOWN_PAYMENT_KEY]: (value) =>
    value <= 1 ? 'sempre' : `entrada + ${value - 1}x`,
};

/** Numbers whose unit is not "x": the delinquency threshold and the withdrawal period are counts of days. */
const VALUE_FORMAT: Record<string, (value: number) => string> = {
  [DELINQUENCY_DAYS_KEY]: (value) => (value === 1 ? '1 dia após o vencimento' : `${value} dias após o vencimento`),
  [WITHDRAWAL_PERIOD_KEY]: (value) =>
    value === 0 ? 'Sem prazo' : value === 1 ? '1 dia após a efetivação' : `${value} dias após a efetivação`,
};

/**
 * A rule's value as the operator reads it: `3x · entrada + 2x` for the down payment rule, `Vendas` for the
 * sales module's `"novo"`, `30 dias após o vencimento` for the delinquency threshold, the bands one after the
 * other for the score bands, the plain number for a rule without a detail.
 *
 * @param rule The rule, for its key.
 * @param value The value.
 */
export function ruleValueLabel(rule: { key: string }, value: RuleValue): string {
  if (Array.isArray(value)) return scoreBandsLabel(rule, value);
  if (typeof value === 'string') return ruleOptionCopy(rule, value).label;

  const format = VALUE_FORMAT[rule.key];
  if (format) return format(value);

  const detail = VALUE_DETAIL[rule.key];
  return detail ? `${value}x · ${detail(value)}` : String(value);
}

/**
 * Score bands as one line: `0–300 Aprovação manual · 301–1000 Liberar`, in the order the scores run.
 *
 * @param rule The rule, for the actions' names.
 * @param bands The bands, in any order.
 */
export function scoreBandsLabel(rule: { key: string }, bands: ScoreBandValue[]): string {
  if (bands.length === 0) return 'Nenhuma faixa';

  return [...bands]
    .sort((a, b) => a.de - b.de)
    .map((band) => `${band.de}–${band.ate} ${ruleOptionCopy(rule, band.acao).label}`)
    .join(' · ');
}

/**
 * What the rule accepts, said before the operator chooses — the server checks it again either way. A range
 * for an integer rule, the options for a choice rule.
 *
 * @param rule The rule.
 */
export function ruleRangeLabel(rule: {
  key: string;
  valueType: RuleValueType;
  minimum: number | null;
  maximum: number | null;
  options: string[];
}): string {
  if (rule.valueType === 'Choice') {
    const labels = rule.options.map((option) => ruleOptionCopy(rule, option).label);
    return `Opções: ${joinAlternatives(labels)}`;
  }

  if (rule.valueType === 'ScoreBands') {
    const labels = rule.options.map((option) => ruleOptionCopy(rule, option).label);
    return `Faixas de ${rule.minimum} a ${rule.maximum}, sem buraco nem sobreposição · Ações: ${joinAlternatives(labels)}`;
  }

  return `Aceita de ${rule.minimum} a ${rule.maximum}`;
}

/**
 * When a change reaches what the clinic sees, per rule. The sales module is read at login — the menu stays
 * in the clinic's session cookie for up to a day — while the v1.1's API applies it at once; every other
 * rule is read at the next sale.
 */
const RATING_REACH = {
  default:
    'Vale para todas as empresas sem exceção própria, a partir da próxima análise de crédito de uma venda. As '
    + 'vendas já analisadas continuam com a avaliação que tiveram.',
  override: (defaultLabel: string) =>
    `Vale só para esta empresa, a partir da próxima análise de crédito — mesmo que o padrão da TMR (${defaultLabel}) `
    + 'mude depois. As vendas já analisadas continuam com a avaliação que tiveram.',
};

const REACH: Record<string, { default: string; override: (defaultLabel: string) => string }> = {
  [CREDIT_RATING_MODE_KEY]: RATING_REACH,
  [DELINQUENCY_DAYS_KEY]: RATING_REACH,
  [DELINQUENT_DEBT_ACTION_KEY]: RATING_REACH,
  [SCORE_BANDS_KEY]: RATING_REACH,
  [WITHDRAWAL_PERIOD_KEY]: {
    default:
      'Vale na hora para todas as empresas sem exceção própria, inclusive nos pedidos já efetivados que pedirem '
      + 'cancelamento. O prazo impresso no contrato e nos e-mails acompanha na próxima venda da clínica.',
    override: (defaultLabel) =>
      `Vale na hora só para esta empresa, inclusive nos pedidos já efetivados, mesmo que o padrão da TMR (${defaultLabel}) `
      + 'mude depois. O prazo impresso no contrato e nos e-mails acompanha na próxima venda dela.',
  },
  [SALES_MODULE_KEY]: {
    default:
      'Vale para todas as empresas sem exceção própria. O menu do Portal da Clínica muda no próximo login dos '
      + 'usuários, e a API já segue na hora. As empresas com exceção continuam com o módulo delas.',
    override: (defaultLabel) =>
      `Vale só para esta empresa, mesmo que o padrão da TMR (${defaultLabel}) mude depois. O menu do Portal da `
      + 'Clínica muda no próximo login dos usuários dela — peça que saiam e entrem de novo. A API já segue na hora.',
  },
};

/**
 * One sentence on what saving reaches, shown in the dialog above the buttons.
 *
 * @param rule The rule, with the TMR's default the override sentence names.
 * @param scope Whether the TMR's default or one Empresa's own value is being saved.
 */
export function ruleReach(rule: { key: string; defaultValue: RuleValue }, scope: 'default' | 'override'): string {
  const defaultLabel = ruleValueLabel(rule, rule.defaultValue);
  const specific = REACH[rule.key];

  if (scope === 'default') {
    return specific?.default
      ?? 'Vale para todas as empresas sem exceção própria, a partir da próxima venda. As empresas com exceção continuam com o valor delas.';
  }

  return specific?.override(defaultLabel)
    ?? `Vale só para esta empresa, a partir da próxima venda — mesmo que o padrão da TMR (${defaultLabel}) mude depois.`;
}

/**
 * How many Empresas do not follow the default — the number that says how far a change of default reaches.
 *
 * @param count Empresas with their own value.
 */
export function overrideCountLabel(count: number): string {
  if (count === 0) return 'Nenhuma empresa com exceção';
  return count === 1 ? '1 empresa com exceção' : `${count} empresas com exceção`;
}

/**
 * Who set the default and when.
 *
 * Three cases, and the difference matters: nothing stored (the platform's fallback), stored by the migration
 * (no editor), or set by a person.
 *
 * @param rule The rule.
 */
export function defaultOriginLabel(rule: Pick<BusinessRule, 'updatedAt' | 'updatedByName'>): string {
  if (!rule.updatedAt) return 'Padrão da plataforma';
  if (!rule.updatedByName) return 'Padrão inicial da plataforma';
  return `Alterado em ${formatDay(rule.updatedAt)} por ${rule.updatedByName}`;
}

/**
 * Who set an Empresa's own value and when — the same sentence on its tab and on the rule's list.
 *
 * @param at When it was set, or null.
 * @param by Who set it, or null.
 */
export function overrideOriginLabel(at: string | null, by: string | null): string {
  const who = by ? ` por ${by}` : '';
  const when = at ? ` em ${formatDay(at)}` : '';
  return `Definida${who}${when}`;
}

/**
 * Whether the Empresa follows the default, and whose value applies.
 *
 * @param item The rule as it applies to the Empresa.
 */
export function companyRuleSituation(item: CompanyBusinessRule): string {
  if (!item.overridden) return 'Segue o padrão da TMR';

  const who = item.overriddenByName ? ` por ${item.overriddenByName}` : '';
  const when = item.overriddenAt ? ` em ${formatDay(item.overriddenAt)}` : '';
  return `Exceção desta empresa, definida${who}${when}`;
}

/**
 * Why this Empresa cannot have overrides — or null when it can.
 *
 * The v1.1 finds an override by `produtores.id`, and the server refuses one for an Empresa without it — the
 * same limitation the patient contract overrides have (`24` §5). Saying so beats offering a button that
 * ends in an error.
 *
 * @param rules The Empresa's rules as the server answered.
 */
export function overrideIneligibility(rules: Pick<CompanyBusinessRules, 'legacyLinked'>): string | null {
  if (rules.legacyLinked) return null;

  return 'Esta empresa ainda não foi replicada para o sistema legado. As vendas dela são feitas por lá, e sem '
    + 'o cadastro do outro lado uma exceção não teria onde valer. Ela segue o padrão da TMR.';
}

/**
 * A date as `21/09/2026`, in the operator's timezone.
 *
 * @param iso The ISO timestamp from the API.
 */
export function formatDay(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(date);
}

/** `A, B ou C` — the way a sentence lists alternatives in PT-BR. */
function joinAlternatives(items: string[]): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} ou ${items[items.length - 1]}`;
}
